/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useState, useEffect } from 'react';
import {
    object, string, func, shape,
} from 'prop-types';
import { bindActionCreators, compose } from 'redux';
import { connect } from 'react-redux';
import { useQuery } from '@apollo/client';
import mbpLogger from 'mbp-logger';
import { uid } from 'react-uid';

import {
    TextField, Grid, Typography, Button,
} from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';

import findHasPersonalization from '../../../../../../gql/queries/findHasPersonalization';
import { getOrderItemErrors } from '../../../../../../../state/ducks/Checkout/ducks/Common/ducks/Error/Error-Selector';
import ErrorMessage from '../ErrorMessage/ErrorMessage';
import updatePersonalization from '../../../../../../../state/ducks/MfeCheckout/ducks/CartItem/Personalization/Personalization-Actions';

const styles = ({ palette }) => ({
    personalizeWrapper: {
        marginRight: '16px',
        marginBottom: '10px',
    },
    personalizeText: {
        textAlign: 'right',
        paddingRight: '15px',
    },
    totalCharCount: {
        fontSize: '1em',
    },
    content: {
        justifyItems: 'center',
        textAlign: 'center',
    },
    enterTextLabel: {
        color: palette.common.grey,
    },
    inputTextField: {
        width: '100%',
    },
    btnCancel: {
        backgroundColor: palette.primaryButton,
        color: palette.colorPrimaryContrast,
        fontSize: '1em',
        fontWeight: 'bold',
        margin: '10px auto',
        display: 'block',
        width: '110px',
        '&:hover, &:focus, &:active': {
            backgroundColor: palette.primaryButton,
        },
    },
    btnEdit: {
        backgroundColor: palette.cms?.primary || palette.colorPrimary,
        color: palette.colorPrimaryContrast,
        fontSize: '1em',
        fontWeight: 'bold',
        margin: '10px auto',
        display: 'block',
        width: '110px',
        '&:hover, &:focus, &:active': {
            backgroundColor: palette.cms?.primary || palette.colorPrimary,
        },
    },
    continueButtonDisable: {
        color: palette.cms?.white || '#ffffff',
        backgroundColor: palette.cms?.gray || '#ccc',
        fontSize: '1em',
        fontWeight: 'bold',
        margin: '10px auto',
        display: 'block',
        width: '110px',
        padding: '8px 10px',
        '&:hover': {
            backgroundColor: palette.cms?.gray || '#ccc',
        },
    },
});

const PersonalizationForm = ({
    classes, orderItemErrors, onCloseModal, callUpdatePersonalization, orderItem,
}) => {
    if (!orderItem || !orderItem.product || !orderItem.personalization?.personalizationType) { return null; }

    const [totalCharacterLines, setTotalCharacterLines] = useState(0);
    const [personalizedTextArray, setPersonalizedTextArray] = useState([]);

    const orderItemId = orderItem.cartItemId;
    const parentSkuCode = orderItem.product?.parentProductCode;
    const brandId = orderItem.product?.brandCode;
    const defaultError = orderItemErrors?.[orderItemId]?.personalization?.errorMessage || '';
    const validationErrors = orderItemErrors?.[orderItemId]?.recipient?.validationErrors || {};

    useEffect(() => {
        if (personalizedTextArray.length === 0 && orderItem?.comments) {
            const messagePers = orderItem.comments.replace('MessageTxt=', '').split('|');
            setPersonalizedTextArray(messagePers);
        } else if (orderItem.personalization?.personalizationType === 'PRODUCT_PERSONALIZATION') {
            setPersonalizedTextArray(orderItem.personalization.personalizationLines);
        }
    }, []);

    useEffect(() => {
        let totalCharacter = 0;
        personalizedTextArray.forEach((item) => {
            if (typeof item === 'object' && item.perAttributeValue) {
                totalCharacter += item.perAttributeValue.length;
            } else if (typeof item === 'string' && item) {
                totalCharacter += item.length;
            }
        });

        if (totalCharacter !== totalCharacterLines) {
            setTotalCharacterLines(totalCharacter);
        }
    }, [personalizedTextArray]);

    const PERSONALIZATION_QUERY = findHasPersonalization(brandId, parentSkuCode);
    const { error, loading, data } = useQuery(PERSONALIZATION_QUERY);

    if (loading) {
        return null;
    }

    if (error) {
        mbpLogger.logError({
            appName: process.env.npm_package_name,
            jsError: error,
            component: 'PersonalizationForm.js',
            message: `Failed to fetch content data for: parentSkuCode ${parentSkuCode} query findHasPersonalization on brand id ${brandId}`,
        });
    }

    if (data) {
        const personalization = data.findProductPageByPartNumber?.product?.productSkus?.[0]?.personalization;
        const continueButtonDisabled = (personalization?.indicator === '3' && totalCharacterLines === 0);

        const updatePersonalizedProdData = () => {
            const personalizationData = {};
            if (personalizedTextArray) {
                const personalizationLines = [];
                let indexLine = 1;
                personalizedTextArray.forEach((item) => {
                    if (
                        (typeof item === 'object' && item.perAttributeValue)
                        || (typeof item === 'string' && item)
                    ) {
                        personalizationLines.push({
                            perAttributeId: indexLine,
                            perAttributeValue: typeof item === 'object' ? item.perAttributeValue : item,
                        });
                        indexLine += 1;
                    }
                });

                personalizationData.personalizationType = 'PRODUCT_PERSONALIZATION';
                personalizationData.personalizationLines = personalizationLines;

                const reqObj = {
                    quantity: null,
                    orderItemId,
                    personalization: personalizationData,
                    handleCallback: onCloseModal,
                };

                callUpdatePersonalization(reqObj);
            }
        };

        const handleChangePersonalizeText = (event, index) => {
            const personalizedTextChanged = [...personalizedTextArray];
            personalizedTextChanged[index] = event.target.value;
            setPersonalizedTextArray(personalizedTextChanged);
        };

        let maxCharacters = 0;
        if (personalization?.lines) {
            const { lines } = personalization;
            lines.forEach((line) => {
                maxCharacters += line?.maxChars;
            });
        } else {
            maxCharacters = 10;
        }

        const buildInputLines = () => {
            const isMandatory = personalization?.indicator === '3';
            return (
                (personalization?.lines || orderItem.personalization?.personalizationLines)?.map((line, index) => {
                    let value = '';

                    if (typeof personalizedTextArray[index] === 'object' && personalizedTextArray[index].perAttributeValue) {
                        value = personalizedTextArray[index].perAttributeValue;
                    } else if (typeof personalizedTextArray[index] === 'string' && personalizedTextArray[index]) {
                        value = personalizedTextArray[index];
                    }

                    return (
                        <TextField
                            key={uid(line, index)}
                            className={classes.inputTextField}
                            required={isMandatory}
                            id={`perzline-${index + 1}`}
                            label={`Line ${index + 1}`}
                            value={value}
                            onChange={(e) => { handleChangePersonalizeText(e, index); }}
                            inputProps={{
                                maxLength: line?.maxChars || 10,
                            }}
                            InputLabelProps={{
                                classes: {
                                    root: classes.enterTextLabel,
                                    focused: 'focused',
                                },
                            }}
                        />
                    );
                })
            );
        };

        return (
            <Grid container className={classes.personalizeWrapper}>
                <Grid item xs={12} className={classes.content}>{buildInputLines()}</Grid>
                <Grid item xs={12} className={classes.content}>
                    <Typography className={classes.totalCharCount}>{`Character count: ${totalCharacterLines}/${maxCharacters} `}</Typography>
                </Grid>
                {
                    (defaultError || validationErrors.personalizationLines)
                    && (
                        <Grid item xs={12} md={12} lg={12}>
                            <ErrorMessage
                                noShadow
                                error={defaultError || validationErrors.personalizationLines}
                                align="center"
                            />
                        </Grid>
                    )
                }
                <Grid container>
                    <Grid item xs={6}>
                        <Button
                            id="btn-pznline-cancel"
                            data-test="cart-btn-cancel"
                            name="cancelButton"
                            className={classes.btnCancel}
                            aria-label="Cancel"
                            onClick={() => onCloseModal(false)}
                        >
                            Cancel
                        </Button>
                    </Grid>
                    <Grid item xs={6}>
                        <Button
                            id="btn-pznline-edit"
                            data-test="personalization-btn-edit"
                            name="editPersonalizationButton"
                            className={`${continueButtonDisabled ? classes.continueButtonDisable : classes.btnEdit}`}
                            aria-label="Edit"
                            disabled={continueButtonDisabled}
                            onClick={() => updatePersonalizedProdData()}
                        >
                            Update
                        </Button>
                    </Grid>
                </Grid>
            </Grid>
        );
    }
    return null;
};

PersonalizationForm.propTypes = {
    classes: object.isRequired,
    onCloseModal: func.isRequired,
    callUpdatePersonalization: func.isRequired,
    orderItemErrors: object.isRequired,
    orderItem: shape({
        sku: string,
    }).isRequired,
};

const mapStateToProps = (state) => ({
    orderItemErrors: getOrderItemErrors(state),
});
const mapDispatchToProps = (dispatch) => ({
    callUpdatePersonalization: bindActionCreators(updatePersonalization, dispatch),
});
// Compose HOCs
export default compose(
    withStyles(styles),
    connect(mapStateToProps, mapDispatchToProps),
)(PersonalizationForm);
